<script setup lang="ts">
import type { SiteResourceDetailed, SitePullResource } from "@carnet/api/backoffice";

const props = defineProps<{
  detail: SiteResourceDetailed | SitePullResource;
}>();

const { t } = useI18n();
const slots = useSlots();
const { attributes, habitats, localFeatures } = useAppSiteDetailView(() => props.detail);
</script>

<template>
  <AppSection class="bg-elevated" :ui="{ base: 'pt-10 pb-16 md:pb-20' }">
    <div class="grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-[auto_1fr] gap-x-10 gap-y-10 lg:gap-y-6 items-start">
      <div>
        <h1 data-testid="site-detail-title" class="text-6xl font-semibold not-last:mb-4">{{ detail.name }}</h1>

        <p v-if="detail.nearbyCityLabel" class="text-xl font-semibold text-blue-700 not-last:mb-3">{{ detail.nearbyCityLabel }}</p>

        <UBadge v-if="['waiting_for_validation', 'in_review'].includes(detail.validationStatus)" variant="soft" color="error" class="not-last:mb-3">{{
          $t(`site.validationStatus.${detail.validationStatus}`)
        }}</UBadge>

        <div v-if="detail.activityTypes?.length" class="flex flex-wrap gap-2">
          <UBadge v-for="type in detail.activityTypes" :key="type.code" variant="soft" color="neutral" :leading-icon="activityTypeIcon(type.code)">
            {{ type.label }}
          </UBadge>
        </div>
      </div>

      <div v-if="slots.map" class="relative sm:rounded-2xl -mx-4 sm:mx-0 overflow-hidden lg:row-span-2 max-w-2xl">
        <slot name="map" />
      </div>

      <div>
        <dl v-if="attributes.length || habitats.length || localFeatures.length" class="text-base leading-tight divide-y divide-blue-400 border-b border-blue-400">
          <div class="py-4 grid grid-cols-2 gap-x-4 items-baseline" v-for="attribute in attributes" :key="attribute.key">
            <dt class="uppercase text-lg font-semibold">{{ attribute.label }}</dt>
            <dd>{{ attribute.value }}</dd>
          </div>

          <div class="py-4 grid grid-cols-2 gap-x-4 items-baseline" v-if="habitats.length">
            <dt class="uppercase text-lg font-semibold">{{ t("site.habitats") }}</dt>
            <dd class="flex flex-col gap-1">
              <span v-for="habitat in habitats" :key="habitat.code">{{ habitat.label }}</span>
            </dd>
          </div>

          <div class="py-4 grid grid-cols-2 gap-x-4 items-baseline" v-if="localFeatures.length">
            <dt class="uppercase text-lg font-semibold">{{ t("site.localFeatures") }}</dt>
            <dd class="flex flex-col gap-1">
              <span v-for="localFeature in localFeatures" :key="localFeature.code">{{ localFeature.label }}</span>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  </AppSection>
</template>
